import { formatDistanceToNow } from 'date-fns';
import { Box, Typography, Divider } from '@mui/material';
import { ClutchBreakdown } from '../services/ClutchService';
import {
  FaArrowTrendUp,
  FaFire,
  FaBullhorn,
  FaCircleXmark,
  FaChartLine,
  FaFaceFrownOpen,
  FaCrown,
  FaRoad,
  FaTrophy,
  FaRoadCircleCheck,
  FaRegChessKing
} from 'react-icons/fa6';
import { IoNewspaperOutline } from "react-icons/io5";
import './TeamBreakdownCard.css';
import { FaBalanceScale, FaHome } from 'react-icons/fa';

type Props = {
  updatedAt: number;
  teamId: string;
  teamName: string;
  teamLogo?: string;
  score: number;
  breakdown: ClutchBreakdown['breakdown'];
};

export default function TeamBreakdownCard({ updatedAt, teamId, teamName, score, breakdown, teamLogo }: Props) {
  const {
    last10 = 0,
    winStreak = 0,
    runDiff = 0,
    injuries = 0,
    hypeNews = 0,
    antiHype = 0,
    underdogBoost = 0,
    eliteBonus = 0,
    homeFieldBonus = 0,
    fatiguePenalty = 0,
    dominanceBonus = 0,
    balancedTeamBonus = 0,
    roadWarriorBonus = 0,
    divisionFavoriteBonus = 0,
    wildcardChaserBonus = 0,
    hypeHits = [],
    antiHypeHits = [],
    neutralHits = [],
  } = breakdown ?? {};

  return (
    <Box className="team-breakdown-card">
      <Box className="breakdown-lockup" display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <img src={teamLogo} alt={teamName} className="team-breakdown-logo" />
          <Box>
            <h4 className="hype-score-title">
              HypeScore: {Number.isFinite(score) ? score.toFixed(1) : '–'}
            </h4>
            <Typography variant="caption" className="last-updated">
              Last updated: {formatDistanceToNow(new Date(updatedAt), { addSuffix: true })}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider />

      <Box className="breakdown-metrics-grid" mt={1}>
        <Metric label="Last 10 Wins" value={last10} icon={<FaArrowTrendUp />} />
        <Metric label="Win Streak" value={winStreak} icon={<FaFire />} />
        <Metric label="Run Differential" value={runDiff} icon={<FaChartLine />} />
        <Metric label="Injury Penalty" value={injuries} icon={<FaFaceFrownOpen />} />
        <Metric label="Hype Boost" value={hypeNews} icon={<FaBullhorn />} />
        <Metric label="Anti Hype Penalty" value={antiHype} icon={<FaCircleXmark />} />
        <Metric label="Elite Bonus" value={eliteBonus} icon={<FaCrown />} />
        <Metric label="Dominance Bonus" value={dominanceBonus} icon={<FaRegChessKing />} />
        <Metric label="Underdog Boost" value={underdogBoost} icon={<FaBullhorn />} />
        <Metric label="Home Field Bonus" value={homeFieldBonus} icon={<FaHome />} />
        <Metric label="Road Warrior Bonus" value={roadWarriorBonus} icon={<FaRoadCircleCheck />} />
        <Metric label="Balanced Team Bonus" value={balancedTeamBonus} icon={<FaBalanceScale />} />
        <Metric label="Fatigue Penalty" value={fatiguePenalty} icon={<FaFaceFrownOpen />} />
        <Metric label="Division Leader Bonus" value={dominanceBonus} icon={<FaTrophy />} />
        <Metric label="Division Favorite Bonus" value={divisionFavoriteBonus} icon={<FaTrophy />} />
        <Metric label="Wildcard Chaser Bonus" value={wildcardChaserBonus} icon={<FaRoad />} />
      </Box>

      {(hypeHits.length > 0 || antiHypeHits.length > 0 || neutralHits.length > 0) && (
        <Box mt={3}>
          <Divider />
          <Typography variant="subtitle1" mt={2} mb={1}>
            <IoNewspaperOutline /> News Headlines
          </Typography>

          {hypeHits.length > 0 && (
            <>
              <div className="headline-section-label positive">Positive</div>
              <ul className="headline-list">
                {hypeHits.map((item, i) => (
                  <li key={`hype-${i}`} className="headline-item positive">
                    <a href={item.url} target="_blank" rel="noopener noreferrer">
                      {item.headline}
                    </a>
                    <span className="headline-source">({item.source})</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {neutralHits.length > 0 && (
            <>
              <div className="headline-section-label neutral">Neutral</div>
              <ul className="headline-list">
                {neutralHits.map((item, i) => (
                  <li key={`neutral-${i}`} className="headline-item neutral">
                    <a href={item.url} target="_blank" rel="noopener noreferrer">
                      {item.headline}
                    </a>
                    <span className="headline-source">({item.source})</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {antiHypeHits.length > 0 && (
            <>
              <div className="headline-section-label negative">Negative</div>
              <ul className="headline-list">
                {antiHypeHits.map((item, i) => (
                  <li key={`anti-${i}`} className="headline-item negative">
                    <a href={item.url} target="_blank" rel="noopener noreferrer">
                      {item.headline}
                    </a>
                    <span className="headline-source">({item.source})</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

function Metric({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: React.ReactElement;
}) {
  const isNegative = value < 0;
  return (
    <Box className="metric" display="flex" alignItems="center" gap={1}>
      <span className="metric-icon">{icon}</span>
      <span className="metric-label">{label}</span>
      <span className={`metric-value ${isNegative ? 'negative' : 'positive'}`}>
        {value > 0 ? `+${value}` : value}
      </span>
    </Box>
  );
}
